<template>
  <div v-if="show" class="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black bg-opacity-50">
    <div class="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[80vh] overflow-hidden">
      <!-- 头部 -->
      <div class="px-6 py-4 border-b border-gray-200">
        <div class="flex items-center justify-between">
          <h3 class="text-lg font-medium text-red-600">{{ modalTitle }}</h3>
          <button @click="handleClose" class="text-gray-400 hover:text-gray-600">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>
      </div>

      <!-- 内容 -->
      <div class="px-6 py-4 max-h-96 overflow-y-auto">
        <div class="flex items-start space-x-3">
          <div class="flex-shrink-0">
            <svg class="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.232 16.5c-.77.833.192 2.5 1.732 2.5z"/>
            </svg>
          </div>
          <div class="flex-1">
            <p class="text-sm text-gray-700 mb-4">
              {{ contentPrefix }}包含 <span class="font-medium text-red-600">{{ totalWords }}</span> 个禁止使用的敏感词，{{ actionPrefix }}：
            </p>

            <!-- 敏感词列表 -->
            <div class="space-y-2">
              <div 
                v-for="word in detectedWords" 
                :key="`${word.word}-${word.position}`"
                class="flex items-center justify-between py-2 px-3 bg-red-50 rounded border border-red-200"
              >
                <div class="flex items-center space-x-2">
                  <span class="font-medium text-gray-900">{{ word.word }}</span>
                  <span class="px-2 py-0.5 text-xs rounded bg-red-200 text-red-800">
                    禁止使用
                  </span>
                </div>
                <span class="text-xs text-gray-500">
                  {{ word.position === 'subject' ? '主题' : '内容' }}
                </span>
              </div>
            </div>

            <div class="mt-4 p-3 bg-red-50 border border-red-200 rounded">
              <p class="text-sm text-red-700">
                {{ instructionText }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- 底部按钮 -->
      <div class="px-6 py-4 border-t border-gray-200 bg-gray-50">
        <div class="flex items-center justify-end">
          <button
            @click="handleClose"
            class="px-4 py-2 text-sm text-white bg-red-600 rounded hover:bg-red-700"
          >
            {{ buttonText }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  show: {
    type: Boolean,
    default: false
  },
  sensitiveWordData: {
    type: Object,
    default: () => ({})
  },
  mode: {
    type: String,
    default: 'save', // 'save' 为保存模板模式, 'send' 为发送邮件模式
    validator: (value) => ['save', 'send'].includes(value)
  }
})

const emit = defineEmits(['close'])

// 计算属性：提取敏感词列表
const detectedWords = computed(() => {
  if (!props.sensitiveWordData || !props.sensitiveWordData.detected_words) {
    return []
  }
  
  let words = props.sensitiveWordData.detected_words
  
  // 处理可能的 ErrorDetail 格式
  if (Array.isArray(words)) {
    return words.map(word => ({
      word: extractValue(word.word),
      severity_level: extractValue(word.severity_level),
      category_name: extractValue(word.category_name),
      match_type: extractValue(word.match_type),
      matched_text: extractValue(word.matched_text),
      position: extractValue(word.position),
      start_pos: extractValue(word.start_pos),
      end_pos: extractValue(word.end_pos),
      alternative_words: word.alternative_words || []
    }))
  }
  
  return []
})

// 计算属性：总敏感词数
const totalWords = computed(() => {
  return detectedWords.value.length
})

// 计算属性：动态文字
const modalTitle = computed(() => {
  return props.mode === 'send' ? '无法发送邮件' : '无法保存模板'
})

const contentPrefix = computed(() => {
  return props.mode === 'send' ? '邮件' : '模板'
})

const actionPrefix = computed(() => {
  return props.mode === 'send' ? '无法发送' : '无法保存'
})

const instructionText = computed(() => {
  return props.mode === 'send' ? '请修改邮件内容，移除上述敏感词后再发送。' : '请修改模板内容，移除上述敏感词后再保存。'
})

const buttonText = computed(() => {
  return '知道了'
})

// 提取值的辅助函数（处理 ErrorDetail 格式）
const extractValue = (value) => {
  if (!value) return ''
  
  // 如果是 ErrorDetail 对象，提取字符串值
  if (typeof value === 'object' && value.string !== undefined) {
    return value.string
  }
  
  // 如果已经是字符串，直接返回
  if (typeof value === 'string') {
    return value
  }
  
  return String(value)
}

// 处理关闭
const handleClose = () => {
  emit('close')
}
</script>

<style scoped>
/* 简洁的滚动条 */
.overflow-y-auto::-webkit-scrollbar {
  width: 4px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: transparent;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: #d1d5db;
  border-radius: 2px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: #9ca3af;
}
</style>
